import axios from 'axios';
import { BMT_API_PATH, BMT_ME_STALE_MS } from '@/constants/bmt';
import {
  harnessAnswerCg,
  harnessBookMakeUp,
  harnessChangeDates,
  harnessClearReflection,
  harnessEnrol,
  harnessReflection,
  harnessSaveReflection,
  harnessHomework,
  harnessMe,
  harnessResources,
  harnessSubmit,
  harnessUnsubmit,
  isBmtHarnessEnabled,
} from '@/utils/bmt-harness';

/**
 * BMT Companion — the trainee's side of BMT: sign-up, dates and check-in state
 * (`/me`) and homework. hogc-one-api gates every route on the `bmt-companion`
 * flag and answers 404 while it is off. Errors carry `{ statusCode, code,
 * message }`; the screens switch on `code` through utils/bmt-display.
 */

// DEV-ONLY: the /bmt-mock console's fixtures instead of the API. Gated on BOTH
// the harness toggle and a non-production build, like the voucher harness.
const harnessActive = () => isBmtHarnessEnabled() && process.env.VUE_APP_NODE_ENV !== 'production';

const url = path => `${process.env.VUE_APP_API_URL}${BMT_API_PATH}${path}`;
const authHeaders = rootState => ({
  headers: { Authorization: `Bearer ${rootState?.user?.token || ''}` },
});

/** The API's error code, or null for a network failure. */
export const errorCodeOf = error => error?.response?.data?.code || null;

const INITIAL_STATE = {
  me: null,
  /** Date.now() of the last successful /me read, for the 60-second staleness check. */
  meFetchedAt: 0,
  homework: [],
  homeworkLoaded: false,
  /** NOT_ENROLLED / NOT_APPROVED from /homework: the page shows why, not an error. */
  homeworkBlockedCode: null,
  /** /resources: `{ general, days }`, or null until read. */
  resources: null,
  resourcesLoading: false,
  loading: false,
  homeworkLoading: false,
  /** Ids of homework with a submit/unsubmit in flight, so its button can disable. */
  savingIds: [],
  error: null,
  /**
   * The last BMT check-in scan's outcome, for the /bmt/check-in screen:
   * `{ status, code, display, checkIn, opensAt }` (utils/qr-handler.js).
   */
  lastCheckIn: null,
  /** The private Reflection: `{ areas: [{ key, text }], keptAt }`, or null until read. */
  reflection: null,
  /**
   * Bumped by RESET_STATE (sign-out, the mock console). An action captures it
   * before awaiting and commits nothing if it has moved, so a response for the
   * previous member can never land in the next one's state.
   */
  generation: 0,
};

const state = () => ({ ...INITIAL_STATE, savingIds: [] });

/** True while no RESET_STATE has run since `generation` was captured. */
const isCurrent = (state, generation) => state.generation === generation;

const getters = {
  enrolment: state => state.me?.enrolment || null,
  isApproved: state => state.me?.enrolment?.status === 'APPROVED',
  isStale: state => now => !state.me || now - state.meFetchedAt > BMT_ME_STALE_MS,
  isSaving: state => id => state.savingIds.includes(id),
};

const mutations = {
  SET_ME(state, me) {
    state.me = me;
    state.meFetchedAt = Date.now();
  },
  SET_HOMEWORK(state, items) {
    state.homework = Array.isArray(items) ? items : [];
    state.homeworkLoaded = true;
    state.homeworkBlockedCode = null;
  },
  SET_HOMEWORK_BLOCKED(state, code) {
    state.homework = [];
    state.homeworkLoaded = true;
    state.homeworkBlockedCode = code;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_RESOURCES(state, resources) {
    state.resources = resources || { general: [], days: [] };
  },
  SET_RESOURCES_LOADING(state, loading) {
    state.resourcesLoading = loading;
  },
  SET_HOMEWORK_LOADING(state, loading) {
    state.homeworkLoading = loading;
  },
  SET_SAVING(state, { id, saving }) {
    state.savingIds = saving
      ? [...new Set([...state.savingIds, id])]
      : state.savingIds.filter(other => other !== id);
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_REFLECTION(state, reflection) {
    state.reflection = reflection || { areas: [], keptAt: null };
  },
  SET_LAST_CHECK_IN(state, result) {
    state.lastCheckIn = result || null;
  },
  RESET_STATE(state) {
    Object.assign(state, INITIAL_STATE, { savingIds: [], generation: state.generation + 1 });
  },
};

/** The API (or the harness) for each call, so the actions read the same either way. */
const api = {
  async me(rootState) {
    if (harnessActive()) return harnessMe();
    const { data } = await axios.get(url('/me'), authHeaders(rootState));
    return data;
  },
  async enrol(rootState, body) {
    if (harnessActive()) return harnessEnrol(body);
    const { data } = await axios.post(url('/enrolment'), body, authHeaders(rootState));
    return data;
  },
  async changeDates(rootState, body) {
    if (harnessActive()) return harnessChangeDates(body);
    const { data } = await axios.put(url('/enrolment/dates'), body, authHeaders(rootState));
    return data;
  },
  async bookMakeUp(rootState, body) {
    if (harnessActive()) return harnessBookMakeUp(body);
    const { data } = await axios.post(url('/makeups'), body, authHeaders(rootState));
    return data;
  },
  async reflection(rootState) {
    if (harnessActive()) return harnessReflection();
    const { data } = await axios.get(url('/reflection'), authHeaders(rootState));
    return data;
  },
  async saveReflection(rootState, body) {
    if (harnessActive()) return harnessSaveReflection(body);
    const { data } = await axios.put(url('/reflection'), body, authHeaders(rootState));
    return data;
  },
  async clearReflection(rootState) {
    if (harnessActive()) return harnessClearReflection();
    const { data } = await axios.delete(url('/reflection'), authHeaders(rootState));
    return data;
  },
  async answerCg(rootState, body) {
    if (harnessActive()) return harnessAnswerCg(body);
    const { data } = await axios.put(url('/enrolment/cg-note'), body, authHeaders(rootState));
    return data;
  },
  async resources(rootState) {
    if (harnessActive()) return harnessResources();
    const { data } = await axios.get(url('/resources'), authHeaders(rootState));
    return data;
  },
  async homework(rootState) {
    if (harnessActive()) return harnessHomework();
    const { data } = await axios.get(url('/homework'), authHeaders(rootState));
    return data;
  },
  async submit(rootState, id, body) {
    if (harnessActive()) return harnessSubmit(id, body);
    const { data } = await axios.post(url(`/homework/${id}/submit`), body, authHeaders(rootState));
    return data;
  },
  async unsubmit(rootState, id) {
    if (harnessActive()) return harnessUnsubmit(id);
    const { data } = await axios.delete(url(`/homework/${id}/submit`), authHeaders(rootState));
    return data;
  },
};

const HOMEWORK_BLOCKED_CODES = ['NOT_ENROLLED', 'NOT_APPROVED'];

const actions = {
  /**
   * Read /me. With `{ ifStale: true }` it skips the request when the last read
   * is under a minute old — page enter and app resume pass that; a scan or an
   * explicit retry does not.
   */
  async fetchMe({ commit, getters, state, rootState }, { ifStale = false } = {}) {
    if (ifStale && !getters.isStale(Date.now())) return state.me;
    const { generation } = state;
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const me = await api.me(rootState);
      if (!isCurrent(state, generation)) return null;
      commit('SET_ME', me);
      return me;
    } catch (error) {
      if (isCurrent(state, generation)) commit('SET_ERROR', errorCodeOf(error) || 'NETWORK');
      throw error;
    } finally {
      if (isCurrent(state, generation)) commit('SET_LOADING', false);
    }
  },

  /**
   * Sign up for an intake. `cgNote` is required when /me said `needsCgCheck`.
   * The response is a fresh MeView (PENDING or APPROVED). A closed intake
   * re-reads /me so the list of open intakes is current.
   */
  async enrol({ commit, dispatch, state, rootState }, { intakeId, cgNote, dates } = {}) {
    const body = { intakeId };
    if (cgNote) body.cgNote = cgNote;
    if (Array.isArray(dates)) body.dates = dates;
    const { generation } = state;
    try {
      const me = await api.enrol(rootState, body);
      if (!isCurrent(state, generation)) return null;
      commit('SET_ME', me);
      return me;
    } catch (error) {
      // Awaited, so the sign-up form remounts against the list without it.
      if (errorCodeOf(error) === 'INTAKE_NOT_OPEN' && isCurrent(state, generation)) {
        await dispatch('fetchMe').catch(() => {});
      }
      throw error;
    }
  },

  /** "Change my attendance": replace the dates the trainee will attend. Returns a fresh MeView. */
  async changeDates({ commit, state, rootState }, dates) {
    const { generation } = state;
    const me = await api.changeDates(rootState, { dates });
    if (!isCurrent(state, generation)) return null;
    commit('SET_ME', me);
    return me;
  },

  /** Pick the make-up date for a missed date (MU1). Returns a fresh MeView. */
  async bookMakeUp({ commit, state, rootState }, { missedDate, intakeId, date } = {}) {
    const { generation } = state;
    const me = await api.bookMakeUp(rootState, { missedDate, intakeId, date });
    if (!isCurrent(state, generation)) return null;
    commit('SET_ME', me);
    return me;
  },

  /** Read the private Reflection (approved trainees only). */
  async fetchReflection({ commit, state, rootState }) {
    const { generation } = state;
    const reflection = await api.reflection(rootState);
    if (isCurrent(state, generation)) commit('SET_REFLECTION', reflection);
    return reflection;
  },

  /** Keep the Reflection: `areas` is `[{ key, text }]`. Returns what the API kept. */
  async saveReflection({ commit, state, rootState }, areas) {
    const { generation } = state;
    const reflection = await api.saveReflection(rootState, { areas });
    if (isCurrent(state, generation)) commit('SET_REFLECTION', reflection);
    return reflection;
  },

  async clearReflection({ commit, state, rootState }) {
    const { generation } = state;
    const reflection = await api.clearReflection(rootState);
    if (isCurrent(state, generation)) commit('SET_REFLECTION', reflection);
    return reflection;
  },

  /** A pending walk-in with no CG answers the CG question once. Returns a fresh MeView. */
  async answerCg({ commit, state, rootState }, cgNote) {
    const { generation } = state;
    const me = await api.answerCg(rootState, { cgNote });
    if (!isCurrent(state, generation)) return null;
    commit('SET_ME', me);
    return me;
  },

  /** Read /resources (approved trainees only). */
  async fetchResources({ commit, state, rootState }) {
    const { generation } = state;
    commit('SET_RESOURCES_LOADING', true);
    try {
      const resources = await api.resources(rootState);
      if (isCurrent(state, generation)) commit('SET_RESOURCES', resources);
      return resources;
    } finally {
      if (isCurrent(state, generation)) commit('SET_RESOURCES_LOADING', false);
    }
  },

  /** Read /homework. A trainee not (yet) approved gets a reason, not an error. */
  async fetchHomework({ commit, state, rootState }) {
    const { generation } = state;
    commit('SET_HOMEWORK_LOADING', true);
    try {
      const items = await api.homework(rootState);
      if (!isCurrent(state, generation)) return [];
      commit('SET_HOMEWORK', items);
      return items;
    } catch (error) {
      const code = errorCodeOf(error);
      if (HOMEWORK_BLOCKED_CODES.includes(code)) {
        if (isCurrent(state, generation)) commit('SET_HOMEWORK_BLOCKED', code);
        return [];
      }
      throw error;
    } finally {
      if (isCurrent(state, generation)) commit('SET_HOMEWORK_LOADING', false);
    }
  },

  /**
   * Tick a TICK item, or submit (or replace) a written answer: `text`, or the
   * testimony's four `sections`. Returns the new list.
   */
  async submitHomework({ commit, state, rootState }, { id, text, sections } = {}) {
    const { generation } = state;
    commit('SET_SAVING', { id, saving: true });
    try {
      const body = {};
      if (Array.isArray(sections)) body.sections = sections;
      else if (typeof text === 'string') body.text = text;
      const items = await api.submit(rootState, id, body);
      if (!isCurrent(state, generation)) return [];
      commit('SET_HOMEWORK', items);
      return items;
    } finally {
      if (isCurrent(state, generation)) commit('SET_SAVING', { id, saving: false });
    }
  },

  /** Untick a TICK item, or withdraw a TEXT answer. Returns the new list. */
  async unsubmitHomework({ commit, state, rootState }, id) {
    const { generation } = state;
    commit('SET_SAVING', { id, saving: true });
    try {
      const items = await api.unsubmit(rootState, id);
      if (!isCurrent(state, generation)) return [];
      commit('SET_HOMEWORK', items);
      return items;
    } finally {
      if (isCurrent(state, generation)) commit('SET_SAVING', { id, saving: false });
    }
  },

  reset({ commit }) {
    commit('RESET_STATE');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
